/**
 * Inline icon set.
 *
 * The prototypes carry a ~48-symbol inline SVG sprite; the handoff says a production build
 * should replace it with the codebase's existing icon system. There is no existing icon system
 * in this repo, so this is it — kept small, stroke-based, and sized in `em` so icons scale with
 * their label. Add symbols here rather than pasting inline SVG into surfaces.
 */

export type IconName =
  | 'towns'
  | 'feed'
  | 'search'
  | 'signals'
  | 'history'
  | 'trends'
  | 'ask'
  | 'account'
  | 'lock'
  | 'plus'
  | 'more'
  | 'pin'
  | 'zap'
  | 'landmark'
  | 'cap'
  | 'map'
  | 'gavel'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-down'
  | 'play'
  | 'check'
  | 'quote'
  | 'arrow-right'
  | 'credit'
  | 'carry'
  | 'calendar';

const PATHS: Record<IconName, string> = {
  towns: 'M3 21V9l9-6 9 6v12M9 21v-6h6v6',
  feed: 'M4 5h16M4 12h16M4 19h10',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.35-4.35',
  signals: 'M4 12a8 8 0 0 1 8-8M4 12a8 8 0 0 0 8 8M12 12h.01M16 8l4-4M16 16l4 4',
  history: 'M6 3h9l5 5v13H6zM10 3v6h9M9 13h7M9 17h5',
  trends: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  ask: 'M21 15a3 3 0 0 1-3 3H8l-5 4V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3z',
  account: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
  plus: 'M12 5v14M5 12h14',
  more: 'M4 6h16M4 12h16M4 18h16',
  pin: 'M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11ZM12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  zap: 'M13 2 4 14h7l-1 8 9-12h-7l1-8Z',
  landmark: 'M3 10h18M5 10v8M10 10v8M14 10v8M19 10v8M3 21h18M12 3 3 8h18l-9-5Z',
  cap: 'M12 4 2 9l10 5 10-5-10-5ZM6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5',
  map: 'm9 4-6 3v13l6-3 6 3 6-3V4l-6 3-6-3ZM9 4v13M15 7v13',
  gavel: 'm14 4 6 6M17 7 7 17M10 14l-6 6M8 12l4 4M4 20h8',
  'chevron-left': 'M15 18l-6-6 6-6',
  'chevron-right': 'M9 18l6-6-6-6',
  'chevron-down': 'M6 9l6 6 6-6',
  play: 'M6 4l14 8-14 8V4Z',
  check: 'M4 12l6 6L20 6',
  quote: 'M9 7H5v6h4l-2 4h3l2-4V7Zm10 0h-4v6h4l-2 4h3l2-4V7Z',
  // Taken from the plan-change design's own sprite so the screen matches it exactly.
  'arrow-right': 'M5 12h14M13 6l6 6-6 6',
  credit: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 12h8',
  carry: 'M3 8h18v13H3zM3 8l2-4h14l2 4M10 13h4',
  calendar: 'M3 5h18v16H3zM3 10h18M8 3v4M16 3v4',
};

interface IconProps {
  name: IconName;
  /** Any CSS length; defaults to 1em so the icon tracks its label's size. */
  size?: string;
  className?: string;
}

export function Icon({ name, size = '1em', className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="miter"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
